import React, { useState } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis,
} from 'recharts';
import { useAppState } from '../store/AppContext';
import { Card, CardBody } from './ui/Card';
import { getAssetClassBreakdown, getTotalNAV, resolveClassColor } from '../utils/calculations';
import { formatVND } from '../utils/format';
import { Asset, AssetClassMeta } from '../types';

// ── Pyramid tier definitions — dùng class IDs từ DB ──────────────────────────
interface PyramidTier {
  id: string;
  label: string;
  sublabel: string;
  classIds: string[];   // khớp với asset_classes.id
  fillColor: string;
  strokeColor: string;
  textColor: string;
}

const PYRAMID_TIERS: PyramidTier[] = [
  {
    id: 'top',
    label: 'Tầng Đỉnh',
    sublabel: 'Tập trung & Đột biến',
    classIds: ['esop', 'crypto'],
    fillColor: 'rgba(251,113,133,0.22)',
    strokeColor: '#fb7185',
    textColor: '#fb7185',
  },
  {
    id: 'middle',
    label: 'Tầng Giữa',
    sublabel: 'Tăng trưởng & Tích lũy',
    classIds: ['stocks', 'bds', 'real-estate', 'gold'],
    fillColor: 'rgba(56,189,248,0.16)',
    strokeColor: '#38bdf8',
    textColor: '#38bdf8',
  },
  {
    id: 'bottom',
    label: 'Tầng Đáy',
    sublabel: 'An toàn & Thanh khoản',
    classIds: ['cash', 'fixed', 'bonds', 'consumer', 'other'],
    fillColor: 'rgba(16,185,129,0.16)',
    strokeColor: '#10b981',
    textColor: '#10b981',
  },
];

// ── Custom Pie Tooltip ────────────────────────────────────────────────────────
function CustomPieTooltip({ active, payload, total }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0';
  return (
    <div style={{
      background: 'var(--tooltip-bg)',
      border: '1px solid var(--tooltip-bd)',
      borderRadius: 10,
      padding: '10px 14px',
      fontSize: 12,
      minWidth: 170,
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    }}>
      <div style={{ color: d.payload.color, fontWeight: 700, marginBottom: 5, fontSize: 13 }}>
        {d.name}
      </div>
      <div style={{ color: 'var(--tx-1)', fontWeight: 700, fontSize: 15, marginBottom: 3, textShadow: 'none' }}>
        {formatVND(d.value)}
      </div>
      <div style={{ color: 'var(--tx-4)' }}>
        Tỷ trọng: <span style={{ color: 'var(--tx-3)', fontWeight: 600 }}>{pct}%</span>
      </div>
    </div>
  );
}

// ── Wealth Pyramid SVG ────────────────────────────────────────────────────────
function WealthPyramid({ assets }: { assets: Asset[] }) {
  const [hoveredTier, setHoveredTier] = useState<string | null>(null);
  const total = getTotalNAV(assets);

  const tierData = PYRAMID_TIERS.map((tier) => {
    const value = assets
      .filter((a) => tier.classIds.includes(a.assetClassId || ''))
      .reduce((s, a) => s + a.currentPrice * a.quantity, 0);
    return { ...tier, value, pct: total > 0 ? (value / total) * 100 : 0 };
  });

  // SVG geometry
  const W = 240;
  const apexY = 14;
  const baseY = 206;
  const cx = W / 2;       // 120
  const drawH = baseY - apexY; // 192
  const maxHW = cx;        // 120 half-width at base

  const hw = (y: number) => ((y - apexY) / drawH) * maxHW;

  const tierH = drawH / 3;
  const y0 = apexY;
  const y1 = apexY + tierH;
  const y2 = apexY + tierH * 2;
  const y3 = baseY;

  const polygons = [
    // Top (triangle)
    `${cx},${y0} ${cx + hw(y1)},${y1} ${cx - hw(y1)},${y1}`,
    // Middle (trapezoid)
    `${cx - hw(y1)},${y1} ${cx + hw(y1)},${y1} ${cx + hw(y2)},${y2} ${cx - hw(y2)},${y2}`,
    // Bottom (trapezoid)
    `${cx - hw(y2)},${y2} ${cx + hw(y2)},${y2} ${cx + hw(y3)},${y3} ${cx - hw(y3)},${y3}`,
  ];

  const textYCenter = [
    (y0 + y1) / 2,
    (y1 + y2) / 2,
    (y2 + y3) / 2,
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: 12, color: 'var(--tx-4)', marginBottom: 12 }}>Tháp Tài Sản</div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
        {/* SVG */}
        <svg width={W} height={baseY + 10} style={{ flexShrink: 0, overflow: 'visible' }}>
          {tierData.map((tier, i) => {
            const isHovered = hoveredTier === tier.id;
            return (
              <g
                key={tier.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredTier(tier.id)}
                onMouseLeave={() => setHoveredTier(null)}
              >
                <polygon
                  points={polygons[i]}
                  fill={isHovered ? tier.fillColor.replace(/[\d.]+\)$/, '0.38)') : tier.fillColor}
                  stroke={tier.strokeColor}
                  strokeWidth={isHovered ? 2 : 1.2}
                  strokeOpacity={0.7}
                  style={{ transition: 'all 0.15s' }}
                />
                {/* Tier name */}
                <text
                  x={cx}
                  y={textYCenter[i] - 9}
                  textAnchor="middle"
                  fill={tier.textColor}
                  fontSize={9}
                  fontWeight={700}
                  letterSpacing="0.08em"
                >
                  {tier.label.toUpperCase()}
                </text>
                {/* Value */}
                <text
                  x={cx}
                  y={textYCenter[i] + 5}
                  textAnchor="middle"
                  fill="var(--tx-1)"
                  fontSize={11}
                  fontWeight={700}
                >
                  {formatVND(tier.value)}
                </text>
                {/* Percent */}
                <text
                  x={cx}
                  y={textYCenter[i] + 18}
                  textAnchor="middle"
                  fill="var(--tx-4)"
                  fontSize={10}
                >
                  {tier.pct.toFixed(1)}%
                </text>
              </g>
            );
          })}
          {/* Divider lines */}
          <line x1={cx - hw(y1)} y1={y1} x2={cx + hw(y1)} y2={y1} stroke="var(--bd)" strokeWidth={1} />
          <line x1={cx - hw(y2)} y1={y2} x2={cx + hw(y2)} y2={y2} stroke="var(--bd)" strokeWidth={1} />
        </svg>

        {/* Tier legend */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tierData.map((tier) => (
            <div
              key={tier.id}
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                border: `1px solid ${tier.strokeColor}28`,
                background: hoveredTier === tier.id
                  ? tier.fillColor.replace(/[\d.]+\)$/, '0.28)')
                  : tier.fillColor,
                cursor: 'default',
                transition: 'background 0.15s',
              }}
              onMouseEnter={() => setHoveredTier(tier.id)}
              onMouseLeave={() => setHoveredTier(null)}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: tier.textColor, fontWeight: 700 }}>
                  {tier.label}
                </span>
                <span style={{ fontSize: 12, color: tier.textColor, fontWeight: 700 }}>
                  {tier.pct.toFixed(1)}%
                </span>
              </div>
              <div style={{ fontSize: 10, color: 'var(--tx-3)', marginBottom: 5 }}>
                {tier.sublabel}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx-1)' }}>
                {formatVND(tier.value)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Rebalancing / Drift Analysis ─────────────────────────────────────────────
interface DriftRow {
  name: string;
  color: string;
  cost: number;
  market: number;
  designPct: number;
  currentPct: number;
  gap: number;
  action: string;
  actionColor: string;
}

function buildDriftRows(assets: Asset[], assetClasses: AssetClassMeta[]): DriftRow[] {
  const map: Record<string, { cost: number; market: number; color: string }> = {};

  assets.forEach(a => {
    // ONLY use market value (currentPrice × quantity) — never include income/projections
    const cls = a.assetClass || 'Khác';
    if (!map[cls]) map[cls] = { cost: 0, market: 0, color: resolveClassColor(cls, assetClasses) };
    map[cls].cost   += a.costPrice   * a.quantity;   // Giá vốn (vốn gốc)
    map[cls].market += a.currentPrice * a.quantity;  // Thị giá (market value only)
  });

  const totalCost   = Object.values(map).reduce((s, v) => s + v.cost,   0);
  const totalMarket = Object.values(map).reduce((s, v) => s + v.market, 0);

  return Object.entries(map).map(([name, v]) => {
    const designPct  = totalCost   > 0 ? (v.cost   / totalCost)   * 100 : 0;
    const currentPct = totalMarket > 0 ? (v.market / totalMarket) * 100 : 0;
    const gap        = currentPct - designPct;
    const action      = gap > 5  ? 'Quá tỷ trọng — Chốt lời'
                      : gap < -5 ? 'Dưới tỷ trọng — Mua thêm'
                      :            'Cân bằng';
    const actionColor = gap > 5 ? '#f97316' : gap < -5 ? '#38bdf8' : '#64748b';
    return { name, color: v.color, cost: v.cost, market: v.market, designPct, currentPct, gap, action, actionColor };
  }).sort((a, b) => b.gap - a.gap);  // Most over-weight first
}

function RebalancingTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--tooltip-bg)',
      border: '1px solid var(--tooltip-bd)',
      borderRadius: 10,
      padding: '10px 14px',
      fontSize: 12,
    }}>
      <div style={{ fontWeight: 700, color: 'var(--tx-2)', marginBottom: 6 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: p.fill || p.color }} />
          <span style={{ color: 'var(--tx-4)' }}>{p.name}:</span>
          <span style={{ color: 'var(--tx-1)', fontWeight: 600 }}>{Number(p.value).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

function RebalancingAnalysis({ assets, assetClasses }: { assets: Asset[]; assetClasses: AssetClassMeta[] }) {
  const rows = buildDriftRows(assets, assetClasses);
  if (rows.length === 0) return null;

  const chartData = rows.map(r => ({
    name: r.name,
    'Thiết kế': parseFloat(r.designPct.toFixed(1)),
    'Hiện tại': parseFloat(r.currentPct.toFixed(1)),
    actionColor: r.actionColor,
  }));

  const barH = Math.max(rows.length * 44 + 20, 120);

  return (
    <div>
      {/* Title */}
      <div style={{ borderBottom: '1px solid var(--bd)', marginBottom: 20, paddingBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-1)' }}>Phân tích Tái cơ cấu (Rebalancing)</div>
        <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 3 }}>
          Độ lệch = Tỷ trọng Hiện tại (thị giá) − Tỷ trọng Thiết kế (giá vốn)
        </div>
      </div>

      {/* Horizontal bar chart */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--tx-4)' }}>Biểu đồ so sánh Thiết kế vs Hiện tại (%)</div>
          {/* Manual legend — tránh bị clip bởi ResponsiveContainer */}
          <div style={{ display: 'flex', gap: 14 }}>
            {[{ label: 'Thiết kế', color: '#475569' }, { label: 'Hiện tại', color: '#38bdf8' }].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: l.color }} />
                <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={barH}>
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 0, right: 44, left: 0, bottom: 0 }}
            barCategoryGap="28%"
            barGap={2}
          >
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={v => `${v}%`}
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              axisLine={false}
              tickLine={false}
              width={130}
              tick={({ x, y, payload }: any) => (
                <text
                  x={x - 6}
                  y={y}
                  dy={4}
                  textAnchor="end"
                  fill="#cbd5e1"
                  fontSize={11}
                  fontFamily="inherit"
                >
                  {payload.value}
                </text>
              )}
            />
            {/* cursor={false} để tránh block mouse events trên vertical layout */}
            <Tooltip content={<RebalancingTooltip />} cursor={false} />
            <Bar dataKey="Thiết kế" fill="#475569" radius={[0, 3, 3, 0]} barSize={9} isAnimationActive={false} />
            <Bar dataKey="Hiện tại" radius={[0, 3, 3, 0]} barSize={9} isAnimationActive={false}>
              {chartData.map((d, i) => (
                <Cell key={i} fill={d.actionColor} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Drift table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--bd)' }}>
              {[
                { label: 'Lớp Tài sản', align: 'left'  },
                { label: 'Giá Vốn',     align: 'right' },
                { label: 'Thiết kế %',  align: 'right' },
                { label: 'Thị Giá',     align: 'right' },
                { label: 'Hiện tại %',  align: 'right' },
                { label: 'Độ lệch',     align: 'right' },
                { label: 'Hành động',   align: 'right' },
              ].map(h => (
                <th key={h.label} style={{
                  padding: '6px 10px', color: 'var(--tx-4)', fontWeight: 700,
                  textAlign: h.align as any, fontSize: 10,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.name} style={{ borderBottom: '1px solid var(--bd)' }}>
                {/* Lớp tài sản */}
                <td style={{ padding: '10px 10px', textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: r.color, flexShrink: 0 }} />
                    <span style={{ color: 'var(--tx-2)', fontWeight: 600 }}>{r.name}</span>
                  </div>
                </td>
                {/* Giá vốn */}
                <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--tx-4)', fontSize: 11 }}>
                  {formatVND(r.cost)}
                </td>
                {/* Thiết kế % */}
                <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--tx-3)', fontWeight: 600 }}>
                  {r.designPct.toFixed(1)}%
                </td>
                {/* Thị giá */}
                <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--tx-4)', fontSize: 11 }}>
                  {formatVND(r.market)}
                </td>
                {/* Hiện tại % */}
                <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--tx-3)', fontWeight: 600 }}>
                  {r.currentPct.toFixed(1)}%
                </td>
                {/* Độ lệch */}
                <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                  <span style={{ color: r.actionColor, fontWeight: 700, fontSize: 13 }}>
                    {r.gap > 0 ? '+' : ''}{r.gap.toFixed(1)}%
                  </span>
                </td>
                {/* Hành động */}
                <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 9px',
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: 700,
                    color: r.actionColor,
                    background: `${r.actionColor}18`,
                    border: `1px solid ${r.actionColor}30`,
                    whiteSpace: 'nowrap',
                  }}>
                    {r.action}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Module ───────────────────────────────────────────────────────────────
export function AssetStructureModule() {
  const { state } = useAppState();
  const { assets, buckets } = state;
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);

  const filteredAssets = selectedBucket
    ? assets.filter((a) => a.bucketId === selectedBucket)
    : assets;

  const classData = getAssetClassBreakdown(filteredAssets, state.assetClasses);
  const total = getTotalNAV(filteredAssets);

  const activeBucket = buckets.find((b) => b.id === selectedBucket);

  return (
    <Card>
      {/* Header + Fund filter */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--bd)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>Cơ cấu Tài sản</span>
            <span style={{ fontSize: 11, color: 'var(--tx-4)', marginLeft: 10 }}>
              {activeBucket ? activeBucket.name : 'Tất cả gia sản'} ·{' '}
              <span style={{ color: '#10b981', fontWeight: 600 }}>{formatVND(total)}</span>
            </span>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <FilterTab
              label="Tất cả"
              active={!selectedBucket}
              color="#10b981"
              onClick={() => setSelectedBucket(null)}
            />
            {buckets.map((b) => (
              <FilterTab
                key={b.id}
                label={b.name}
                active={selectedBucket === b.id}
                color={b.color}
                onClick={() => setSelectedBucket(b.id)}
              />
            ))}
          </div>
        </div>
      </div>

      <CardBody style={{ padding: '20px 20px 16px' }}>
        <div className="grid-cols-2-md" style={{ alignItems: 'start', gap: 24 }}>

          {/* ── Pie Chart ── */}
          <div>
            <div style={{ fontSize: 12, color: 'var(--tx-4)', marginBottom: 14 }}>Phân bổ theo Lớp Tài sản</div>

            {classData.length === 0 ? (
              <div style={{
                textAlign: 'center', color: 'var(--tx-4)', fontSize: 13,
                padding: '60px 0', border: '1px dashed var(--bd)', borderRadius: 10,
              }}>
                Không có tài sản nào trong quỹ này
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                {/* Pie */}
                <div style={{ width: 176, height: 176, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={classData}
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={80}
                        paddingAngle={1}
                        dataKey="value"
                        stroke="var(--bg-card)"
                        strokeWidth={2}
                        isAnimationActive={true}
                      >
                        {classData.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={<CustomPieTooltip total={total} />}
                        wrapperStyle={{ zIndex: 100 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {classData.map((d) => {
                    const pct = total > 0 ? ((d.value / total) * 100) : 0;
                    return (
                      <div key={d.name}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div style={{
                              width: 8, height: 8, borderRadius: 2,
                              backgroundColor: d.color, flexShrink: 0,
                            }} />
                            <span style={{ fontSize: 12, color: 'var(--tx-3)' }}>{d.name}</span>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-2)' }}>
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--tx-3)', paddingLeft: 15 }}>
                          {formatVND(d.value)}
                        </div>
                        {/* Mini progress bar */}
                        <div style={{
                          height: 2, borderRadius: 1, marginTop: 4, marginLeft: 15,
                          backgroundColor: 'var(--bd)',
                        }}>
                          <div style={{
                            height: '100%', borderRadius: 1,
                            width: `${pct}%`,
                            backgroundColor: d.color,
                            opacity: 0.7,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Wealth Pyramid ── */}
          <WealthPyramid assets={filteredAssets} />
        </div>
      </CardBody>

      {/* ── Rebalancing Section ── */}
      <div style={{ borderTop: '1px solid var(--bd)', padding: '20px 20px 24px' }}>
        <RebalancingAnalysis assets={filteredAssets} assetClasses={state.assetClasses} />
      </div>
    </Card>
  );
}

// ── Filter Tab Button ─────────────────────────────────────────────────────────
function FilterTab({
  label, active, color, onClick,
}: { label: string; active: boolean; color: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 11px',
        borderRadius: 20,
        border: `1px solid ${active ? color : 'var(--bd)'}`,
        background: active ? `${color}1a` : 'var(--bg-card-el)',
        color: active ? color : 'var(--tx-3)',
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap' as const,
      }}
    >
      {label}
    </button>
  );
}
